import axios from "axios";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { LS } from "@/constants/index";
import { handleSuccessfulAuthentication } from "@/pages/Auth/utils";
import { ROUTES } from "@/router";
import { useAppStore } from "@/store/app/slice";
import { AuthState } from "@/types/common";
import { Environment, getApiUrl, getEnvironment } from "@/utils/getEnvironment";

const isPublicAuthRoute = (pathname: string): boolean => {
  return (
    pathname.includes(`/${ROUTES.AUTH}`) ||
    pathname === `/${ROUTES.REGISTER}` ||
    pathname === `/${ROUTES.SIGN_OUT}`
  );
};

export const useAppInitialization = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Create a fresh axios instance without interceptors
        const plainAxios = axios.create({
          baseURL: getApiUrl(),
          withCredentials: getEnvironment() !== Environment.Local,
        });

        // Get refresh token for localhost
        const refreshToken =
          getEnvironment() === Environment.Local
            ? localStorage.getItem(LS.RefreshToken)
            : null;

        // Try to refresh the token directly (no interceptors)
        const refreshResponse = await plainAxios.post(
          "/api/auth/refreshToken",
          refreshToken ? { refreshToken } : {},
        );

        const { accessToken, refreshToken: newRefreshToken } =
          refreshResponse.data;

        await handleSuccessfulAuthentication(accessToken, newRefreshToken);
      } catch (error) {
        useAppStore.getState().setAuthenticated(AuthState.Unauthenticated);

        if (getEnvironment() === Environment.Local) {
          localStorage.removeItem(LS.RefreshToken);
        }

        // Save current page before redirecting to sign-in
        const currentPagePathname =
          window.location.pathname +
          window.location.search +
          window.location.hash;

        if (!isPublicAuthRoute(window.location.pathname)) {
          useAppStore.getState().setSavedUrl(currentPagePathname);
        }

        if (window.location.pathname.includes(`/${ROUTES.LEGAL}`)) {
          return;
        }

        if (!isPublicAuthRoute(window.location.pathname)) {
          navigateRef.current(`/${ROUTES.AUTH}/${ROUTES.SIGN_IN}`);
        }
      }
    };

    void initializeAuth();
  }, []);
};
